/**
 * What goes where on Today, decided in a file with no imports.
 *
 * Every question the front page asks — which section a row belongs in, what
 * the greeting says, which seven days get a dot, which rows are the picks — is
 * answered here so that `npm test` can see the answer. `npm test` cannot see
 * a layout (CLAUDE.md), and Today is the screen whose whole content is a set
 * of windowing decisions; a screenshot is the wrong instrument for checking
 * whether an exhibition that closes on Sunday is filed under Last days or
 * Tonight.
 *
 * **No imports, deliberately**, the same rule `rowMeta.ts`, `filmRank.ts`,
 * `away.ts` and `reach.ts` live by: the root suite loads these files directly
 * and Node's ESM resolver will not guess the extension a bundler would, so an
 * `import … from './calendar'` here would make the whole file untestable
 * offline. The date helpers below are `calendar.ts`'s and `rowMeta.ts`'s,
 * retyped for that reason and to the same rules.
 *
 * **Rule 1 of the brief, and B1/H2 of the decisions: no prose is composed
 * here.** `greeting` substitutes into one of `GREETING_TEMPLATES` and
 * `today.test.ts` asserts that every line it can produce is one of them with
 * its slots filled. Nothing else on the screen is a sentence, and nothing
 * from `score_reasons` reaches it.
 *
 * The imports are `./places.ts`, `./search.ts`, `./daylong.ts`,
 * `./rankBase.ts` and `./zone.ts` (C2.1: the zone "today" and a row's own
 * time are read in), named with their extensions so Node loads them, and each
 * dependency-free itself: `aheadCount` is the clock rule's count, and two
 * copies of it would be the drift `clockSource` below already warns about
 * between this file and `rowMeta.ts` (cards WP4). `daylong.ts` is what a
 * festival or market is (on every day of its span, over when its last day is)
 * and `rankBase.ts` what a null score is worth — decisions `entries.ts`,
 * `shortlist.ts`, `browse.ts` and this file must read the same way.
 */
import { aheadCount, type Place } from './places.ts'
import { matchesSearch, type Searchable } from './search.ts'
import { spanCovers, spanDayFrom, spanOf } from './daylong.ts'
import { scoreOf } from './rankBase.ts'
import { clockIn, dayIn, hereZone, instantIn, zoneOfCity } from './zone.ts'

// ---------------------------------------------------------------------------
// The shapes this file reads, declared rather than imported
// ---------------------------------------------------------------------------

/** Where a row is, as `reach.ts` classifies it. */
export type Reach = 'city' | 'day_trip' | 'away'

/**
 * The row fields Today reads.
 *
 * `FeedRow` satisfies this structurally, so a caller passes one straight in —
 * the same arrangement `rowMeta.ts`'s `RowEntry` has, and for the same reason.
 * The brief writes these signatures as `sectionOf(e: FeedRow, …)`; this is
 * that type, declared where the loader can reach it.
 *
 * Two fields are **not** on `FeedRow` and are attached by `Today.tsx` when it
 * builds its list:
 *
 *   - `reach`, because classifying a city means `reach.ts`'s twenty-name
 *     list, which this file may not import and must not copy — a second copy
 *     of a list is a second thing to be wrong;
 *   - `saved`, because savedness lives in `entry_state` and is a fact about
 *     Doug rather than about the row.
 *
 * Both default to the harmless answer when absent (`'city'`, `false`), so a
 * caller that knows neither still gets sections.
 */
export interface RowEntry {
  id: string
  title: string
  type: string
  score?: number | null
  city?: string | null
  district?: string | null
  venue?: string | null
  building?: string | null
  organisation?: string | null
  run_start?: string | null
  run_end?: string | null
  published_on?: string | null
  next_showing?: string | null
  next_opening?: string | null
  showing_count?: number | null
  /** Cards WP4: read through `aheadCount`, never bare (the E5 trap). */
  places?: readonly Place[] | null
  performers?: Record<string, string> | null
  classical?: { programme?: { composer?: string | null; work?: string | null }[] } | null
  /** For the film rule in `sectionOf`'s Last days — `browse.ts`'s own field,
   *  restated for the no-imports rule. */
  discipline?: string | null
  /** `reachOf(cityOf(e))`, resolved by the caller. See above. */
  reach?: Reach
  /** An `entry_state` row says saved. Resolved by the caller. See above. */
  saved?: boolean
  /** E1/E3, for the affinity lift below — `affinity.ts`'s own shape,
   *  restated because this file may not import it. */
  people?: { id: string }[] | null
  ensembles?: { id: string }[] | null
}

/** One showing, structurally — `ShowingLite` and `ShowingRow` both satisfy it. */
export interface Showing {
  starts_at: string
  /** `'run'` marks the span rather than an occurrence, and is not a time. */
  kind?: string | null
}

// ---------------------------------------------------------------------------
// Dates
//
// `calendar.ts`'s `D`/`addDays`, retyped to exactly the same convention: a
// `YYYY-MM-DD` is pinned to noon UTC and read back in UTC, so a date-only
// string never shifts a day for any reader. A timestamp is read in a zone,
// and which zone is the question C2.1 split in two (trap T3): "now" — today,
// tonight, this weekend, the picks window — is read in the current region's
// (`hereZone()`); a row's own time is read in its town's region's
// (`zoneOfCity(e.city)`), because that is the time printed on the ticket.
// `zone.ts` holds both, import-free, and memoises its formatters per zone.
// ---------------------------------------------------------------------------

const D = (s: string) => new Date(`${s}T12:00:00Z`)

const fmtDay = (s: string, o: Intl.DateTimeFormatOptions) =>
  D(s).toLocaleDateString('en-GB', { timeZone: 'UTC', ...o })

/** The calendar day a moment falls on where the person is looking — the
 *  current region's day (Berlin's until C2.2). Not `slice(0, 10)`, which is
 *  the UTC day. The name is older than regions. */
export const berlinDay = (at: Date | string): string => dayIn(at, hereZone())

/** The current region's wall-clock hour, 0–23. */
export const berlinHour = (at: Date | string): number => Number(clockIn(at, hereZone()).slice(0, 2))

/** The current region's wall-clock minute as two digits, `'00'`–`'59'`. */
const berlinMinute = (at: Date | string): string => clockIn(at, hereZone()).slice(3, 5)

/** The day a row's own timestamp falls on, in the row's zone. */
const rowDay = (e: { city?: string | null }, at: string): string => dayIn(at, zoneOfCity(e.city))

const addDaysIso = (s: string, n: number) =>
  new Date(D(s).getTime() + n * 86400000).toISOString().slice(0, 10)

const daysBetween = (a: string, b: string) =>
  Math.round((D(b).getTime() - D(a).getTime()) / 86400000)

/** 0 Sunday … 6 Saturday, for a date-only string. */
const dayOfWeek = (isoDay: string) => D(isoDay).getUTCDay()

/**
 * The instant at which the current region's wall clock reads `hour:00` on
 * `day`.
 *
 * The inverse of `berlinDay`/`berlinHour`, and `picksWindow` needs it: a
 * window that opens at 17:00 is a moment, not a date, and comparing a
 * timestamp against `${day}T17:00:00Z` would be two hours wrong all a Berlin
 * summer. `zone.ts`'s `instantIn` does the two passes.
 */
function berlinInstant(day: string, hour: number): Date {
  return instantIn(day, `${String(hour).padStart(2, '0')}:00`, hereZone())
}

/** The instant `day` begins in the current region — tasks plan D3: Clear
 *  hides what was done before today, so its cutoff is this, never `now()`,
 *  which would take today's rows with it. */
export const dayStart = (day: string): Date => berlinInstant(day, 0)

// ---------------------------------------------------------------------------
// The clock (WP 5.2's own addition: which part of the day this is)
// ---------------------------------------------------------------------------

/** After this hour the day is an evening, and Tonight is the section that
 *  matters. 17:00 is the brief's. */
export const EVENING_HOUR = 17

/** From this hour the day is late — WP 5.2's replacement for
 *  `TONIGHT_ENDS_HOUR` (deleted): where that stopped Tonight from growing any
 *  further, this is the hour the screen turns over to tomorrow instead of
 *  merely freezing. "It's almost midnight Saturday, Saturday night is done,
 *  but I have a bunch of events here from Saturday still" (Doug, 26 Sept) —
 *  the fix is not a later cutoff, it is a different section past this one. */
export const LATE_HOUR = 22

export type Phase = 'morning' | 'afternoon' | 'evening' | 'late'

/** 05–12 morning, 12–17 afternoon, 17–22 evening, 22–05 late. The boundaries
 *  are `EVENING_HOUR` and `LATE_HOUR` restated as a total function over the
 *  clock, so `greeting` and `sectionOf` read one decision instead of two
 *  hour comparisons that could drift apart. */
export function phaseOf(now: Date): Phase {
  const h = berlinHour(now)
  if (h < 5 || h >= LATE_HOUR) return 'late'
  if (h < 12) return 'morning'
  if (h < EVENING_HOUR) return 'afternoon'
  return 'evening'
}

/**
 * Every line `greeting` can return.
 *
 * Exported because the test reads it: rule 1 is checked by asserting that the
 * line is one of these with `{weekday}`, `{place}` and `{name}` replaced by
 * the values that went in. A new shape of greeting has to be added here to
 * pass, which is the point — it makes writing a sentence a visible act, the
 * same way `FIXED_LABELS` does in `rowMeta.ts`.
 */
export const GREETING_TEMPLATES = [
  '{weekday}',
  '{weekday} evening',
  '{weekday} night',
  '{weekday}, in {place}',
  '{weekday} evening, in {place}',
  '{weekday} night, in {place}',
  "{weekday}, {name}'s birthday",
  "{weekday} evening, {name}'s birthday",
  "{weekday} night, {name}'s birthday",
] as const

/** Slot substitution through a replacer function, never a plain string: a
 *  place or a name containing `$&` would otherwise splice the match back into
 *  the output. */
const fill = (template: string, slots: Record<string, string>) =>
  template.replace(/\{(\w+)\}/g, (whole, key: string) => slots[key] ?? whole)

/**
 * The weekday and a state word, and the facts under it.
 *
 * `sub[0]` is always the date. Anything after it is a fact, and the screen
 * joins at most two of them with " · " — the trip and the city-news line are
 * the other two candidates and neither is knowable from here, so they are
 * appended by the caller rather than passed in to be ignored.
 *
 * A birthday appears in the line, or in the facts when the line is already
 * about being away, never in both: a screen that says "Mom's birthday" twice
 * in two lines reads as a bug rather than as emphasis.
 */
export function greeting(
  now: Date,
  ctx: { away: string | null; birthday: string | null },
): { line: string; sub: string[] } {
  const day = berlinDay(now)
  const phase = phaseOf(now)
  const base = phase === 'late' ? '{weekday} night' : phase === 'evening' ? '{weekday} evening' : '{weekday}'
  const template = ctx.away
    ? `${base}, in {place}`
    : ctx.birthday
      ? `${base}, {name}'s birthday`
      : base

  const line = fill(template, {
    weekday: fmtDay(day, { weekday: 'long' }),
    place: ctx.away ?? '',
    name: ctx.birthday ?? '',
  })

  const sub = [fmtDay(day, { day: 'numeric', month: 'long' })]
  if (ctx.birthday && ctx.away) sub.push(`${ctx.birthday}'s birthday`)
  return { line, sub }
}

/**
 * The person out of a birthday commitment's title.
 *
 * `commitments.title` is what Google Calendar hands over and every one of
 * them already carries the word: "Mom's Birthday", "Devon Birthday", "Jack
 * Lancaster birthday". The greeting's template is `{name}'s birthday`, so
 * feeding it the title produces "Mom's Birthday's birthday" — the slot wants
 * a name and the column holds a sentence, which is the one place on this
 * screen a fixed template could still read as generated.
 *
 * Falls back to the title where stripping leaves nothing, so a row somebody
 * called simply "Birthday" says that rather than nothing.
 */
export function birthdayName(title: string): string {
  const name = title.replace(/\s*birthdays?\s*$/i, '').replace(/[’']s?$/, '').trim()
  return name || title
}

// ---------------------------------------------------------------------------
// Which day a row is about
// ---------------------------------------------------------------------------

/** A span with two different ends, or null for everything that happens once.
 *  `run_end === run_start` is a one-day row written the long way.
 *
 *  **A short festival or market is not a run** (`daylong.ts`): it is on for
 *  each of its days, so it is answered for by `occurrenceDay`/`occursOnDay`
 *  like any one-off. Treating it as a run filed it once, on its opening day,
 *  and never on a day it was merely on — Design Börse and the Kürbisfest,
 *  2–4 Oct 2026. */
function runOf(e: RowEntry): { start: string; end: string } | null {
  if (!e.run_start || !e.run_end || e.run_end === e.run_start) return null
  if (spanOf(e)) return null
  return { start: e.run_start, end: e.run_end }
}

/**
 * Which timestamp a row may read a clock from.
 *
 * `rowMeta.ts`'s `clockSource`, and the same rule: `showing_count` counts
 * performances, so an `opening` counts 0 and its `next_opening` is the only
 * hour it has; above 1 there is no single time and naming the first would
 * claim it was *the* one.
 */
function clockSource(e: RowEntry): string | null {
  // Cards WP4: `aheadCount` — a film's future showings over its set, where
  // `showing_count` keeps the film's past weeks since cards WP1.
  const n = aheadCount(e)
  if (n === 1) return e.next_showing ?? null
  if (n === 0) return e.next_opening ?? e.next_showing ?? null
  return null
}

/** The next occurrence still ahead, ignoring run markers — they annotate the
 *  span rather than being one of its performances. */
function nextStart(showings: Showing[] | undefined, now: Date): string | null {
  if (!showings?.length) return null
  const from = now.toISOString()
  let best: string | null = null
  for (const s of showings) {
    if (s.kind === 'run') continue
    if (s.starts_at < from) continue
    if (best === null || s.starts_at < best) best = s.starts_at
  }
  return best
}

/**
 * The one calendar day a row happens on, or null for a run.
 *
 * A row carrying several performances has no time of its own, and the next
 * one still ahead is the useful fact — which only the showings know, which is
 * why they are a parameter to `sectionOf` at all.
 */
function occurrenceDay(e: RowEntry, showings: Showing[] | undefined, now: Date): string | null {
  if (runOf(e)) return null
  // A day-long span row is on every day of its span, so its "one day" is the
  // first of those the screen is still about: today, or tomorrow once Tonight
  // has been retired (`late`), clamped into the span — before it opens that is
  // its opening day, after it closes its last. Never the day of a showing,
  // which is only the day somebody printed a time for.
  if (spanOf(e)) {
    const today = berlinDay(now)
    const from = phaseOf(now) === 'late' ? addDaysIso(today, 1) : today
    return spanDayFrom(e, from, today)
  }
  const at = clockSource(e) ?? nextStart(showings, now)
  if (at) return rowDay(e, at)
  return e.run_start ?? e.published_on ?? null
}

/** The instant a row's one stated occurrence starts, or null where it states
 *  none — midnight included, which is what a dateless extraction writes, not
 *  a time anybody stated (the same call `rowMeta.ts`'s `clockOf` makes).
 *  `sectionOf`'s Tonight test and `alreadyStarted` both read a clock off this row and
 *  both have to agree on what counts as one, so there is one function that
 *  resolves it rather than two hour comparisons that could drift apart. */
function startInstant(e: RowEntry, showings: Showing[] | undefined, now: Date): Date | null {
  // A span row has no hour of its own: the one on a showing is whatever a
  // newsletter printed for its first or last day.
  if (spanOf(e)) return null
  const at = clockSource(e) ?? nextStart(showings, now)
  if (!at) return null
  // Midnight in the row's own zone is the placeholder for "a date, no time".
  return clockIn(at, zoneOfCity(e.city)) === '00:00' ? null : new Date(at)
}

/** Is `day` one of the days this row is on? A span row is on every day of its
 *  span; a run only by opening (a three-month exhibition is not each day's
 *  news); anything else on its one occurrence's day. `sectionOf` reads
 *  `occurrenceDay` and the day-by-day lists read this, so a day a list calls
 *  "on" and a section named for that day cannot disagree. */
function occursOnDay(e: RowEntry, day: string, showings: Showing[] | undefined, now: Date): boolean {
  if (spanOf(e)) return spanCovers(e, day, berlinDay(now))
  const run = runOf(e)
  if (run) return run.start === day
  return occurrenceDay(e, showings, now) === day
}

/**
 * Has this row's one occurrence today already started, by more than
 * `STARTED_GRACE_MIN`?
 *
 * False for a run (a span, not a start time), for a row stating no hour (there
 * is nothing to have started), and for a personal row (a commitment does not
 * end because the clock passed it). This is what empties Saturday night at
 * 23:50 (Doug, 26 Sept): the row is not filtered by a later cutoff, it is
 * gone because it is over, and a 20:00 concert and a matinee that finished
 * hours ago are gone for the same reason and by the same grace either way.
 */
export function alreadyStarted(e: RowEntry, now: Date, showings: Showing[] = []): boolean {
  if (runOf(e) || isPersonal(e)) return false
  const at = startInstant(e, showings, now)
  if (!at) return false
  return (now.getTime() - at.getTime()) / 60000 > STARTED_GRACE_MIN
}

/** Something of Doug's own rather than something the city is putting on.
 *  `Today.tsx` gives a commitment, a birthday and a flight one of these
 *  types when it folds them into the same list as the events, so that one
 *  function decides where everything goes.
 *
 *  A visitor is deliberately **not** one of these (WP 6.2): the prototype's
 *  own `calItems()` never feeds its visitors through the day's sections
 *  either — a visit is a span that can already be under way, not a single
 *  day's occurrence, and forcing it through the same `ahead`/weekend checks
 *  that decide a commitment's day would put "Rachel visiting" in This
 *  weekend with no nights on it whenever her stay happened to start on a
 *  Saturday. Coming up builds its visitor lines straight from `stays`. */
export const PERSONAL_TYPES = ['commitment', 'birthday', 'flight'] as const

const isPersonal = (e: RowEntry) => (PERSONAL_TYPES as readonly string[]).includes(e.type)

// ---------------------------------------------------------------------------
// This weekend
// ---------------------------------------------------------------------------

/** The Saturday and Sunday, as calendar days, or null from Monday to
 *  Wednesday — when the weekend is not yet a thing you are deciding. */
function weekendDays(now: Date): { from: string; to: string } | null {
  const today = berlinDay(now)
  const dow = dayOfWeek(today)
  if (dow >= 1 && dow <= 3) return null
  // Sunday: what is left of the weekend you are standing in, which is today.
  if (dow === 0) return { from: today, to: today }
  if (dow === 6) return { from: today, to: addDaysIso(today, 1) }
  // Thursday or Friday: the coming Saturday and Sunday.
  const sat = addDaysIso(today, 6 - dow)
  return { from: sat, to: addDaysIso(sat, 1) }
}

/**
 * The weekend Today is talking about, or null.
 *
 * **The two `Date`s name calendar days, not instants.** They are noon-UTC
 * anchors, the convention `calendar.ts`'s `D` uses everywhere: noon UTC reads
 * back as the same date in UTC and in Berlin, in both halves of the year, so
 * a caller that formats them gets the right day. A caller that wants an
 * instant wants `from` at 00:00 and `to` at 23:59, and should say so itself
 * rather than reading these as though they already were — one value meaning
 * both a day and a moment is the bug this codebase keeps writing.
 */
export function weekendWindow(now: Date): { from: Date; to: Date } | null {
  const w = weekendDays(now)
  return w ? { from: D(w.from), to: D(w.to) } : null
}

// ---------------------------------------------------------------------------
// Sections (H1)
// ---------------------------------------------------------------------------

/** The heading Today's first section carries: the picks rail's own word
 *  (`picksWindow`'s label) where that is "Today" — a weekend daytime, when
 *  the section is the rest of the day — and "Tonight" otherwise. Late, the
 *  label names tomorrow's weekday, but nothing reaches the section then
 *  (`sectionOf` retires it at `LATE_HOUR`), so "Tonight" is only ever the
 *  heading of an empty list. One word from one function, so the rail and the
 *  section under it cannot call the same window two things. */
export function tonightHeading(now: Date): 'Today' | 'Tonight' {
  return picksWindow(now).label === 'Today' ? 'Today' : 'Tonight'
}

export type Section = 'tonight' | 'tomorrow' | 'weekend' | 'coming' | 'lastdays' | 'openings'

/** How long after a stated start a row still counts as not yet begun.
 *  `browse.ts`'s own `STARTED_GRACE_MIN` (WP 5.4) is the same number for the
 *  same reason — a row a phone user is still walking to should not vanish the
 *  instant the clock passes its door time. */
export const STARTED_GRACE_MIN = 30

/** A run is in its last days when it comes down inside a week. */
export const LAST_DAYS = 7

/** How long a run has to have been, in total, before ending inside the week
 *  makes it a Last days row rather than merely a fact about its end date. A
 *  four-day film week that closes on Wednesday is not "your last chance" —
 *  it is simply how long it always ran. */
export const LAST_DAYS_MIN_RUN = 14

/** The same floor, for a film (`type` screening or release, or `discipline`
 *  film). Doug: "maybe you could show movies that have played for an extended
 *  period and are ending everywhere, but I don't know how we would know
 *  that." The merged run's length is the nearest fact to it, and a week-long
 *  cinema run is ordinary where a three-week one is a farewell. */
export const LAST_DAYS_MIN_FILM_RUN = 21

/** How far Coming up and Openings look. A vernissage a fortnight out is still
 *  a decision; one from last Tuesday is nothing. */
export const AHEAD_DAYS = 14

const isFilm = (e: RowEntry) =>
  e.type === 'screening' || e.type === 'release' || e.discipline === 'film'

/**
 * Which one section a row belongs in, or none.
 *
 * **One section, never two** (rule 3), so the order the tests below are
 * written in is the order of precedence and is itself the decision:
 *
 *  1. **A day trip is the weekend and nothing else** (H5, and Doug's own
 *     "mostly should be reserved for weekend"). Checked first, which is what
 *     keeps a Potsdam opening this evening out of Tonight. Outside the
 *     weekend window it has no place on this screen at all — that is what
 *     "reserved for" means, and Browse still has it.
 *  2. **A started row is gone** (WP 5.2). Checked right after the reach
 *     rules: `alreadyStarted` true is `null`, whatever section the rest of
 *     this function would otherwise have filed it under. This is what empties
 *     Saturday night at 23:50, and it applies just as much to a Saturday row
 *     that would otherwise reach This weekend — a started-but-not-over
 *     Saturday evening is not "the weekend" any more than it is "tonight".
 *  3. **Last days beats Tonight.** An exhibition that is open this evening
 *     and closes on Sunday is filed under the fact that expires: being open
 *     tonight is true of it every evening it has left. A run that has not
 *     opened yet is never in its last days, whatever its end date says, and
 *     nor is one whose whole run was always this short (`LAST_DAYS_MIN_RUN`,
 *     `LAST_DAYS_MIN_FILM_RUN`).
 *  4. **Tonight**, **Tomorrow**, **This weekend**, **Openings**, **Coming
 *     up**, in the order the screen draws them — so a commitment on Saturday
 *     reads under the weekend, where it is, rather than under Coming up,
 *     where it would be a second answer to the same question. **Tomorrow**
 *     only exists from `LATE_HOUR`: before it, tomorrow's rows are the
 *     weekend's or nobody's, as today; from it, Tonight itself is retired
 *     (nothing reaches it any later than `LATE_HOUR`) and a row whose day is
 *     tomorrow is named for it ahead of This weekend, so on a Saturday night
 *     the first thing on screen is Sunday and This weekend holds only what is
 *     left of Saturday — a row that has not started yet, since rule 2 already
 *     took the ones that have.
 *
 * A run only reaches This weekend by *starting* in it. A three-month
 * exhibition overlaps every weekend of its life, and a section that listed
 * all of them would be the Browse lane with a different heading; the one that
 * is ending is Last days' already.
 */
export function sectionOf(e: RowEntry, now: Date, showings: Showing[] = []): Section | null {
  const today = berlinDay(now)
  const tomorrow = addDaysIso(today, 1)
  const weekend = weekendDays(now)
  const run = runOf(e)
  const day = occurrenceDay(e, showings, now)
  const late = phaseOf(now) === 'late'

  // In the weekend window: a run by its opening day, anything else by its own.
  const startsInWeekend = !!weekend && (run
    ? run.start >= weekend.from && run.start <= weekend.to
    : !!day && day >= weekend.from && day <= weekend.to)

  const reach = e.reach ?? 'city'
  if (reach === 'away') return null
  if (reach === 'day_trip') return startsInWeekend ? 'weekend' : null

  if (alreadyStarted(e, now, showings)) return null

  // Already open, coming down inside the week, and long enough a run that
  // ending soon is a real fact about it rather than merely how long it ever
  // ran. **Already open** is the load-bearing half of the first two clauses:
  // without it a film week that opens tomorrow and closes on Wednesday lands
  // under "Last days", which is true of its end date and a lie about the row
  // — the section is the regret list, and nothing you have not had a chance
  // to see yet belongs on it. Caught in the first render against live data,
  // where six of the eight rows said "From 25 Sept".
  if (run && run.start <= today && run.end >= today
      && daysBetween(today, run.end) <= LAST_DAYS
      && daysBetween(run.start, run.end) >= (isFilm(e) ? LAST_DAYS_MIN_FILM_RUN : LAST_DAYS_MIN_RUN)) {
    return 'lastdays'
  }

  if (day === today && !late) {
    // An all-day commitment and an opening are tonight by being today: neither
    // states an hour worth filtering on, and both are the evening's business.
    if (isPersonal(e) || e.type === 'opening') return 'tonight'
    // Today's own window is `picksWindow`'s, not a constant of this
    // function's: from 17:00 on a weekday and on a weekend evening, from
    // midnight on a weekend daytime ("a weekend daytime is the day"), so a
    // Sunday market at 11:00 is the same answer here that the rail above it
    // gives. `alreadyStarted` has already dropped what began long ago.
    //
    // A row that states no hour is kept. Dropping it would hide something
    // real that is on today for want of a field two thirds of sources fill;
    // a span row states none by definition.
    const at = startInstant(e, showings, now)
    if (!at || at.toISOString() >= picksWindow(now).from) return 'tonight'
  }

  if (late && day === tomorrow) return 'tomorrow'

  if (startsInWeekend) return 'weekend'

  const ahead = day && day >= today && daysBetween(today, day) <= AHEAD_DAYS
  if (e.type === 'opening' && ahead) return 'openings'
  if (isPersonal(e) && ahead) return 'coming'

  return null
}

/**
 * Everything happening on one calendar day: single occurrences on it, and
 * runs that open on it.
 *
 * Runs merely in progress are not that day's news (WP 3.2c's reasoning,
 * restated here for the same reason `browse.ts`'s `groupByDay` keeps a run in
 * progress out of any one day's heading) and are left out — a three-month
 * exhibition would otherwise appear under every day of the strip you tap.
 *
 * The same two facts `sectionOf` reads — `runOf` and `occurrenceDay` — so a
 * day this function calls "on" and a section `sectionOf` files under that
 * day's own name never disagree about what "on that day" means (rule 3).
 */
export function rowsOnDay<T extends RowEntry>(
  rows: T[], day: string, now: Date, showings?: Map<string, Showing[]>,
): T[] {
  return rows.filter(e => occursOnDay(e, day, showings?.get(e.id), now))
}

/**
 * The one shape `rowsForDayPlan` needs from `weekAhead.ts`'s own `DayPlan` —
 * duck-typed rather than imported, the same reason `Showing` above is:
 * `weekAhead.ts` has no imports either, and its `DayPlan` already satisfies
 * this structurally, so a caller passes one straight in.
 */
export interface DayWindow {
  day: string
  from: string
  cap: number
  weekend: boolean
}

/** `affinity.ts`'s own K5 constants (WP E4), restated — this file has no
 *  imports at all (see the file header), the same reason `browse.ts` and
 *  `browseRank.ts` restate them too. `test/affinity.test.ts` cross-checks
 *  the values against `affinity.ts`'s own. */
const AFFINITY_PER_UNIT = 0.03
const AFFINITY_CAP = 0.10
const AFFINITY_DISMISS_PENALTY = 0.05

/** `affinity.ts`'s own shape, restated. */
export interface AffinityRow { positive: number; negative: number }
export type AffinityMap = Map<string, AffinityRow>

/** `affinity.ts`'s own `affinityLift`, restated (WP E4, K5) — see that file
 *  for the reasoning. */
export function affinityLift(e: RowEntry, affinity?: AffinityMap): number {
  if (!affinity) return 0
  const seen = new Set<string>()
  let positive = 0
  let dismissedTwice = false
  const entities: { key: string }[] = [
    ...(e.people ?? []).map(p => ({ key: `person:${p.id}` })),
    ...(e.ensembles ?? []).map(s => ({ key: `ensemble:${s.id}` })),
  ]
  for (const { key } of entities) {
    if (seen.has(key)) continue
    seen.add(key)
    const a = affinity.get(key)
    if (!a) continue
    positive += a.positive
    if (a.negative >= 2) dismissedTwice = true
  }
  let lift = Math.min(positive * AFFINITY_PER_UNIT, AFFINITY_CAP)
  if (dismissedTwice) lift -= AFFINITY_DISMISS_PENALTY
  return Math.max(-AFFINITY_DISMISS_PENALTY, Math.min(AFFINITY_CAP, lift))
}

/** `affinity.ts`'s own `effectiveScore`, restated: the score `pickLeads`
 *  and `rowsForDayPlan` gate and sort on, in place of the raw `e.score`.
 *  A null score is `UNSCORED_RANK` (`rankBase.ts`), as in Browse and in
 *  `affinity.ts` — this used to be 0, so Today ranked a row nobody had scored
 *  below one scored as poor. */
export function effectiveScore(e: RowEntry, affinity?: AffinityMap): number {
  return scoreOf(e) + affinityLift(e, affinity)
}

/**
 * Which of the week ahead's day blocks are drawn (a1-04).
 *
 * A day with rows is drawn; an away day is drawn with its place and no rows
 * ("Friday 9 Oct / Away · Wroclaw") — but only while nothing is typed in the
 * search box. A search is asking which events match, and an away day matches
 * nothing by construction: drawing it anyway left a search for "zzqqxx" with
 * one block on screen and no "Nothing matching", because the empty state
 * counted the away block as content. The render and the empty state both
 * read this, so they cannot disagree about whether the screen is empty.
 */
export function dayBlocksToDraw<B extends { shown: readonly unknown[]; away?: unknown }>(
  blocks: readonly B[], query: string,
): B[] {
  const searching = query.trim() !== ''
  return blocks.filter(b => b.shown.length > 0 || (!!b.away && !searching))
}

/**
 * The rows worth showing under one day of the week ahead (WP 8.2).
 *
 * A run counts only by *opening* on `plan.day`, never by merely being in
 * progress — the same restraint `rowsOnDay` already applies, and the whole
 * reason it matters here: a day-by-day view that listed a three-month
 * exhibition under every night of the horizon would be the pile this package
 * exists to avoid. A run already open when the horizon starts, or closing
 * inside it, is `sectionOf`'s Last days already (unchanged, drawn below the
 * nights) and this function has nothing to say about it.
 *
 * Everything else counts by `occurrenceDay`, same as `rowsOnDay`, but also
 * has to fall at or after `plan.from` — the evening on a weeknight, midnight
 * on a weekend day — and must not be `alreadyStarted`: a day block a week out
 * still has to hide a row whose own day has arrived and half-finished by the
 * time you look.
 *
 * The two reach checks are `sectionOf`'s own first two lines, restated for
 * one day instead of a window: a row `reach.ts` marks `'away'` never belongs
 * on this screen at all, and a day trip is reserved for the weekend (Doug, 21
 * September) — `plan.weekend` is the same fact `sectionOf`'s `startsInWeekend`
 * reads for it. Skipping these here would let a Potsdam evening leak onto a
 * Tuesday's block, which is the one thing that rule exists to stop.
 *
 * Ranked by score, a saved row leading its day the way a personal row leads a
 * section in `Today.tsx`'s own sort — the closest thing an event has to
 * "already decided". Capped by `plan.cap` last, so the ranking above decides
 * which rows survive it.
 */
export function rowsForDayPlan<T extends RowEntry>(
  rows: T[], plan: DayWindow, now: Date, showings?: Map<string, Showing[]>, affinity?: AffinityMap,
): T[] {
  const matched = rows.filter(e => {
    const reach = e.reach ?? 'city'
    if (reach === 'away') return false
    if (reach === 'day_trip' && !plan.weekend) return false

    const s = showings?.get(e.id)
    if (!occursOnDay(e, plan.day, s, now)) return false
    if (alreadyStarted(e, now, s ?? [])) return false

    const at = startInstant(e, s, now)
    return !at || at.toISOString() >= plan.from
  })

  matched.sort((a, b) => {
    if (!!a.saved !== !!b.saved) return a.saved ? -1 : 1
    return effectiveScore(b, affinity) - effectiveScore(a, affinity)
  })

  return matched.slice(0, plan.cap)
}

// ---------------------------------------------------------------------------
// Worth a drive (blind spots A3)
// ---------------------------------------------------------------------------

/** How far ahead a day trip is worth mentioning: six weeks, the README's
 *  own horizon for planning a Saturday out of town. */
export const DRIVE_DAYS = 42

/** At most this many: a block, not a second Browse. */
export const DRIVE_MAX = 3

/**
 * The day trips Today mentions in its own block, after the week ahead.
 *
 * A row counts when `reach` says `'day_trip'` (resolved by the caller, the
 * same arrangement as everywhere else in this file) and its `run_start` is
 * today or within `DRIVE_DAYS` of it — a start, not an overlap: a Barberini
 * show that opened in July is not news in October, and the one that opens
 * in November is.
 *
 * Two orders, one for each job. *Which* three: a festival leads, because a
 * festival is the thing you drive for; then the score, with affinity, like
 * every other ranked list here. *In what order* they are drawn: by start,
 * soonest first — the block reads as a calendar of Saturdays out of town
 * ("24 Oct, 3 Oct, 31 Oct" read as a mistake, UI audit a1-22), and rank
 * already did its work by choosing. Empty means the block is not drawn.
 */
export function worthADrive<T extends RowEntry>(rows: T[], today: string, affinity?: AffinityMap): T[] {
  const until = addDaysIso(today, DRIVE_DAYS)
  return rows
    .filter(e => e.reach === 'day_trip' && !!e.run_start && e.run_start >= today && e.run_start <= until)
    .sort((a, b) => {
      const fa = a.type === 'festival', fb = b.type === 'festival'
      if (fa !== fb) return fa ? -1 : 1
      // Soonest breaks a tie: exhibitions and openings carry no score (K4),
      // so for most trip rows today the score says nothing at all.
      return effectiveScore(b, affinity) - effectiveScore(a, affinity)
        || a.run_start!.localeCompare(b.run_start!)
    })
    .slice(0, DRIVE_MAX)
    .sort((a, b) => a.run_start!.localeCompare(b.run_start!))
}

// ---------------------------------------------------------------------------
// The picks (H2, WP 5.3)
// ---------------------------------------------------------------------------

/** What the profile has to say before a row is a pick. The same bar Home's
 *  rail and Browse's previews already draw. */
export const PICK_MIN = 0.7

/** How many cards the rail may hold. Rule 3 of the brief: "the rail is not a
 *  second Browse". Five is a swipe you finish. */
export const PICKS_MAX = 5

/**
 * The window the picks are about, read off the clock.
 *
 * Doug, 26 Sept: "Assume that I'm working on weekdays. For the weekend maybe
 * it should be a few things I could be doing, related to whatever time it is
 * at the moment. Right now, since it's the end of the night, I would expect
 * to see something for tomorrow." Three rules fall out of that, and this
 * function is all three:
 *
 *   - **a weekday is an evening.** Whatever time you look, the thing you can
 *     still act on is tonight, so the window opens at `EVENING_HOUR`.
 *   - **a weekend daytime is the day.** Saturday at 10:00 a matinee is a real
 *     answer, so the window is the whole of today; by the evening it is
 *     `EVENING_HOUR` again, like any other evening.
 *   - **late is tomorrow**, on any day — the same turn-over `sectionOf` makes
 *     at `LATE_HOUR`, and for the same reason: at 23:40 tonight is over and
 *     a screen still offering it is answering yesterday's question.
 *
 * `from` and `to` are **instants**, ISO and therefore comparable as strings,
 * `from` inclusive and `to` exclusive. They are not days: "today from 17:00"
 * has an hour in it and a window that could only name a day would have to
 * carry the hour beside it as a second value meaning half the same thing.
 * `berlinDay(from)` is the one calendar day they cover, which is what a run
 * is tested against — see `inPicksWindow`.
 *
 * **The window never says "has it started".** `from` is 00:00 for a weekend
 * daytime rather than `now`, although the brief calls that case "the rest of
 * today": what makes it the rest of today is `alreadyStarted`, which knows
 * about `STARTED_GRACE_MIN` where a `from` of `now` would not. Two values
 * deciding whether a row is over, disagreeing by half an hour, is the bug
 * this codebase keeps writing.
 *
 * The label is one of two fixed words or a weekday name (rule 1). `'Tomorrow'`
 * is in the declared type because the brief fixed the type that way; nothing
 * returns it, because a rail headed "Sunday" says the same thing and says
 * which day.
 */
export function picksWindow(
  now: Date,
): { from: string; to: string; label: 'Tonight' | 'Today' | 'Tomorrow' | string } {
  const today = berlinDay(now)
  const phase = phaseOf(now)

  // Late: tomorrow, whole day. `today + 1` exactly as `sectionOf` computes its
  // own `tomorrow`, so the rail and the Tomorrow section can never be about
  // two different days. It does mean that at 02:00 on a Sunday the rail is
  // about Monday — that is what `LATE_HOUR` running to 05:00 means, and the
  // two screens agreeing about it matters more than the small hours do.
  if (phase === 'late') {
    const day = addDaysIso(today, 1)
    return {
      from: berlinInstant(day, 0).toISOString(),
      to: berlinInstant(addDaysIso(day, 1), 0).toISOString(),
      label: fmtDay(day, { weekday: 'long' }),
    }
  }

  const dow = dayOfWeek(today)
  const daytimeWeekend = (dow === 0 || dow === 6) && phase !== 'evening'
  return {
    from: berlinInstant(today, daytimeWeekend ? 0 : EVENING_HOUR).toISOString(),
    to: berlinInstant(addDaysIso(today, 1), 0).toISOString(),
    label: daytimeWeekend ? 'Today' : 'Tonight',
  }
}

/**
 * The kind a pick counts as, for the diversity rule below.
 *
 * `hueOf`'s branches (`rowMeta.ts`), restated one for one — this file imports
 * nothing, so neither that nor `browse.ts`'s `kindOf` can be reached from
 * here. Restating `hueOf` rather than `kindOf` is deliberate: the rail draws
 * `metaLine`'s dot on every card, so the thing that makes two cards look like
 * the same kind of evening *is* the hue, and a diversity rule cutting the
 * rows a different way would put two rows with the same coloured dot side by
 * side while calling them different. A `hueOf` branch that splits later has
 * to split here too, and `today.test.ts` is where that would be noticed.
 */
type PickKind = 'film' | 'exhibition' | 'market' | 'opera' | 'theatre' | 'concert' | 'club' | 'quiz' | 'talk'

function pickKind(e: RowEntry): PickKind {
  if (e.type === 'quiz') return 'quiz'
  if (e.type === 'screening' || e.type === 'release' || e.discipline === 'film') return 'film'
  if (e.type === 'exhibition' || e.type === 'opening') return 'exhibition'
  if (e.type === 'market' || e.type === 'festival') return 'market'
  if (e.discipline === 'opera') return 'opera'
  if (e.type === 'performance' || e.discipline === 'theatre' || e.discipline === 'dance') return 'theatre'
  if (e.type === 'concert') return 'concert'
  if (e.type === 'club_night') return 'club'
  return 'talk'
}

/**
 * Is this row inside the window?
 *
 * A run has no instant to compare — it is a span — so it counts by covering
 * the window's calendar day, which is the brief's "a run in progress counts
 * as on the day". Everything else counts by its own start falling inside
 * `[from, to)`, and a row that states no hour at all counts by its day, the
 * same concession `sectionOf` makes for Tonight: two thirds of sources fill
 * the field and dropping the rest would hide something real for want of it.
 */
function inPicksWindow(
  e: RowEntry,
  w: { from: string; to: string },
  day: string,
  now: Date,
  showings: Showing[] | undefined,
): boolean {
  if (spanOf(e)) return spanCovers(e, day, berlinDay(now))
  const run = runOf(e)
  if (run) return run.start <= day && run.end >= day
  const at = startInstant(e, showings, now)
  if (at) {
    const iso = at.toISOString()
    return iso >= w.from && iso < w.to
  }
  return occurrenceDay(e, showings, now) === day
}

/**
 * Up to `PICKS_MAX` rows worth the top of the screen.
 *
 * In the window, at the pick bar, in the city, not already started.
 * Dismissal and language are the caller's filters — every list on this screen
 * runs through them, and a rail offering what the sections refuse would be
 * two apps.
 *
 * **The best of each kind first, then the rest by score.** This is the whole
 * of Doug's complaint: the top of the page was five concerts, which is not a
 * choice, and "highest five by score" produces exactly that on any Tuesday
 * the Philharmonie is playing. So the first pass takes each kind's own best
 * — ordered among themselves by score, because the strongest thing should
 * still lead — and the second pass fills the remaining slots with everything
 * left, by score. `pickKind`'s order is not consulted anywhere: it groups,
 * score ranks. With candidates of three kinds the rail therefore shows all
 * three, and with candidates of one kind it shows five of that one, which is
 * honest rather than diverse.
 *
 * Returns `[]` rather than the best of a bad lot: a rail promoting a 0.4
 * would teach Doug to stop reading the top of the page.
 *
 * Generic in the row so the caller gets its own type back, for the reason
 * `browse.ts`'s own generics give: `RowEntry` is the subset this file reads,
 * and a card needs the picture and the performers that are not on it.
 */
export function pickLeads<T extends RowEntry>(
  rows: T[], now: Date, showings?: Map<string, Showing[]>, affinity?: AffinityMap,
): T[] {
  const w = picksWindow(now)
  const day = berlinDay(w.from)

  const candidates: T[] = []
  for (const e of rows) {
    if ((e.reach ?? 'city') !== 'city') continue
    // Nightlife N6 (D6): a pub quiz is never a pick — a habit, not a
    // decision, and unscored (`notRanked`), so `effectiveScore` alone would
    // already keep it out; said here so an affinity lift on a quiz host
    // cannot put one on the rail. The day list below still shows it on its
    // own evening: that is `sectionOf`, which this does not touch.
    if (e.type === 'quiz') continue
    if (effectiveScore(e, affinity) < PICK_MIN) continue
    const s = showings?.get(e.id)
    if (alreadyStarted(e, now, s ?? [])) continue
    if (!inPicksWindow(e, w, day, now, s)) continue
    candidates.push(e)
  }

  candidates.sort((a, b) => effectiveScore(b, affinity) - effectiveScore(a, affinity))

  // Insertion order is score order, because `candidates` is already sorted —
  // which is what makes the map's own iteration the ranking the brief asks
  // for, with no second sort to get out of step with the first.
  const best = new Map<PickKind, T>()
  const rest: T[] = []
  for (const e of candidates) {
    const k = pickKind(e)
    if (best.has(k)) rest.push(e)
    else best.set(k, e)
  }
  return [...best.values(), ...rest].slice(0, PICKS_MAX)
}

// ---------------------------------------------------------------------------
// The seven-day strip (H3)
//
// WP 6.2: the strip draws the forecast now, not an accent dot for something
// of Doug's own and a grey one for a pick — `dayDots` and the `markedDays`
// helper it alone used are gone with them. `DayStrip.tsx` draws seven dates
// straight off `addDaysIso`; there is no decision left here for `npm test` to
// hold.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// City (H4, WP 6.2): ranked, not merely recent
// ---------------------------------------------------------------------------

// The transit lines a person rides are `profiles.transit_lines` (U1.2); this
// file used to hold a constant naming one line, Doug's own ("it's the U7 as my
// main", 27 September — one line, not a placeholder set). A disruption on a
// line you ride outranks a closure that is nowhere near you, which a plain
// "most recent first" list cannot say. Doug's row carries `{U7}`; everyone
// else starts with none, and `onMyLine` then never matches.

export interface NewsForCity {
  category: string
  lines?: string[]
  affects_from?: string | null
  affects_to?: string | null
  /** The day the Worker saw it — for a row with no dates of its own, the
   *  only clue to whether it is still news (`affectsCityToday`). */
  published_on?: string | null
  title?: string | null
  title_en?: string | null
  summary?: string | null
  summary_en?: string | null
}

/** An open-ended disruption (`affects_from` with no `affects_to`, which is
 *  how the BVG feed says "until further notice") that began longer ago than
 *  this is a standing condition, not news: the U6 replacement service since
 *  2022 and the Köpenick tram works "until 2027" affect today every day, and
 *  leading Today with them would push out everything that changed this week.
 *  Fourteen because that is `useNews`'s own window — the "recent" the query
 *  already means. They stay on `#/city`. */
export const STANDING_DAYS = 14

/** A transport closure with no dates at all (berlin.de, the newsletters:
 *  "Ab dem Abend: Ringbahn und S46 im Westen gesperrt", whose "Wednesday
 *  evening to Monday morning" is prose nothing parses) counts as on today
 *  for this many days after the Worker saw it. Three covers the weekend
 *  closure it usually is, and a marathon notice from last Sunday no longer
 *  leads on Friday. */
export const UNDATED_FRESH_DAYS = 3

/**
 * Words that say a line or a stretch is not running: a closure, a
 * replacement service, a suspension. Not a diversion (`Umleitung`), a stop
 * moved sixty metres (`Haltestelle verlegt`), a skipped stop or a platform
 * change — those are transport, but not what Doug meant by closures (2 Oct:
 * "BVG/S-Bahn, Ringbahn, line closures").
 *
 * German compounds put the word anywhere — `Vollsperrung`,
 * `Schienenersatzverkehr`, `Streckensperrung` — so `sperrung`, `gesperrt`
 * and `ersatzverkehr` match inside a word. `störung` may not, because it is
 * inside `Zerstörung` too; it matches at a word start, `Betriebsstörung`
 * named beside it.
 */
const CLOSURE_WORDS = new RegExp([
  'sperrung', 'gesperrt', 'ersatzverkehr', 'unterbrechung',
  '(?:^|[^a-zäöüß])(?:betriebs)?störung',
  'fährt nicht', 'fahren keine', 'fährt keine', 'keine (?:s-bahnen?|züge|u-bahnen?)', 'kein zugverkehr',
  '\\bclos(?:ed|ures?)\\b', '\\bshut', '\\bnot running\\b', '\\bsuspend',
].join('|'), 'i')

/** A transport row that says a line is not running (`CLOSURE_WORDS`), or a
 *  row the Worker already filed as a closure. */
export function isTransportClosure(n: NewsForCity): boolean {
  if (n.category === 'closure') return true
  if (n.category !== 'transport') return false
  return CLOSURE_WORDS.test([n.title, n.title_en, n.summary, n.summary_en].filter(Boolean).join(' '))
}

/**
 * What a person calls a line, to what the feed calls it (C4.1). Berlin needs
 * none: the profile says `U7` and BVG says `U7`. Boston does: the place says
 * "Fitchburg Line" (what a rider writes, and what `profile_places.
 * transit_lines` holds for Doug's Concord row) and the MBTA's alert says
 * `CR-Fitchburg`, the route id the Worker files under `news.lines`.
 *
 * Keys are lower-case; both sides of the comparison go through `canonLine`, so
 * a place that already says `CR-Fitchburg` or `fitchburg` matches too. A
 * `Map`, not an object literal, because `LINE_ALIASES[name]` on a plain object
 * answers `constructor` and `toString` out of `Object.prototype` — the lookup
 * decides both *whether* a name is an alias and *what* it maps to (CLAUDE.md's
 * alias-map bug). Green is left out: the MBTA files it as `Green-B` to `-E`,
 * and "Green Line" names all four.
 */
export const LINE_ALIASES: ReadonlyMap<string, string> = new Map([
  ['fitchburg line', 'CR-Fitchburg'], ['fitchburg', 'CR-Fitchburg'], ['cr-fitchburg', 'CR-Fitchburg'],
  ['lowell line', 'CR-Lowell'], ['cr-lowell', 'CR-Lowell'],
  ['haverhill line', 'CR-Haverhill'], ['cr-haverhill', 'CR-Haverhill'],
  ['newburyport line', 'CR-Newburyport'], ['rockport line', 'CR-Newburyport'], ['cr-newburyport', 'CR-Newburyport'],
  ['worcester line', 'CR-Worcester'], ['cr-worcester', 'CR-Worcester'],
  ['franklin line', 'CR-Franklin'], ['cr-franklin', 'CR-Franklin'],
  ['providence line', 'CR-Providence'], ['cr-providence', 'CR-Providence'],
  ['red line', 'Red'], ['red', 'Red'],
  ['orange line', 'Orange'], ['orange', 'Orange'],
  ['blue line', 'Blue'], ['blue', 'Blue'],
])

/** A line as the feeds name it: the alias if there is one, else exactly as
 *  given, so Berlin's `U7` and `M10` are compared as they always were. */
export const canonLine = (line: string): string => LINE_ALIASES.get(line.trim().toLowerCase()) ?? line

/** Does this row name one of `lines`? With none, never. (The brief's
 *  `onMyLine(text, lines)` takes the row, not text: the lines are a field of
 *  it.) Compared through `canonLine` on both sides, so a place's "Fitchburg
 *  Line" meets a row's `CR-Fitchburg`. */
export const onMyLine = (n: NewsForCity, lines: readonly string[]) => {
  if (!lines.length) return false
  const mine = new Set(lines.map(canonLine))
  return (n.lines ?? []).some(l => mine.has(canonLine(l)))
}

/**
 * Does this row bear on today — the question Today's City lines are ranked
 * by first (Doug, 2 Oct: "rank by what still affects today")? Also the
 * greeting's one city fact, so the two cannot disagree about what "today"
 * means.
 *
 * - Both dates: today is inside them.
 * - Only `affects_to`: it runs until then, so today is covered while that
 *   has not passed — Doug's own rule for the row with no start.
 * - Only `affects_from`: the BVG feed's "since then, until further notice".
 *   It covers today once begun, unless it began more than `STANDING_DAYS`
 *   ago, which makes it a standing condition rather than today's news.
 * - Neither: only a transport closure the Worker saw in the last
 *   `UNDATED_FRESH_DAYS`. A culture piece published this morning is recent,
 *   not something happening to the city today.
 */
export function affectsCityToday(n: NewsForCity, today: string): boolean {
  const from = n.affects_from ?? null
  const to = n.affects_to ?? null
  if (from && to) return from <= today && to >= today
  if (to) return to >= today
  if (from) return from <= today && daysBetween(from, today) <= STANDING_DAYS
  if (!isTransportClosure(n) || !n.published_on) return false
  return n.published_on <= today && daysBetween(n.published_on, today) <= UNDATED_FRESH_DAYS
}

/** `07:15–08:00` in a Polizei row's summary — the only clock a news row
 *  carries. A hyphen or an en dash; the first range wins. */
const TIME_RANGE = /\b(\d{1,2}):(\d{2})\s*[–-]\s*(\d{1,2}):(\d{2})\b/

/**
 * Is this row's own time window over for the day (Doug, 2 Oct: a
 * demonstration 07:15–08:00 when it is now 10:00 leaves Today)?
 *
 * Only for a row whose last day is today — a march on its own day, the last
 * day of a closure that states its hours. An open-ended row has no last day,
 * and a window that crosses midnight (`22:00–01:30`) has not ended at 23:00
 * however its clocks compare. A row that states no range never ends early.
 * `#/city` does not ask: it still lists the morning's march.
 */
export function cityWindowEnded(n: NewsForCity, now: Date): boolean {
  if (!n.affects_to || n.affects_to !== berlinDay(now)) return false
  const text = [n.summary_en ?? n.summary, n.title_en ?? n.title].filter(Boolean).join(' ')
  const m = TIME_RANGE.exec(text)
  if (!m) return false
  const start = Number(m[1]) * 60 + Number(m[2])
  const end = Number(m[3]) * 60 + Number(m[4])
  if (end <= start) return false
  return berlinHour(now) * 60 + Number(berlinMinute(now)) >= end
}

/**
 * The City section's own order, as a tier rather than a comparator. Lower
 * sorts first. Doug's choice of 2 Oct (UI audit a1-23): what still affects
 * today leads, and among today's things a transport closure leads — the
 * Ringbahn shut to Monday ahead of a 45-minute march.
 *
 *   0  a closure on a line Doug rides, today
 *   1  any other transport closure, today
 *   2  anything else on today (a march still to come, a range covering it)
 *   3  a line Doug rides, not today
 *   4  a transport closure, not today (coming, or undated and older)
 *   5  a big event, not today
 *   6  the rest, and a standing disruption (`STANDING_DAYS`)
 *
 * A finished window is not ranked here — this has no clock —
 * `cityLinesToday` drops it. Rows tied on a tier keep whatever order
 * `useNews` gave them (most recent first), which is the one thing this
 * function does not have to decide.
 */
export function cityRank(n: NewsForCity, today: string, lines: readonly string[]): number {
  const closure = isTransportClosure(n)
  if (affectsCityToday(n, today)) return closure ? (onMyLine(n, lines) ? 0 : 1) : 2
  if (onMyLine(n, lines)) return 3
  // Begun, open-ended and not today: `affectsCityToday` refused it only for
  // being older than `STANDING_DAYS`.
  if (n.affects_from && !n.affects_to && n.affects_from <= today) return 6
  if (closure) return 4
  if (n.category === 'event') return 5
  return 6
}

/** Today's City rows: a window already over for the day dropped, the rest
 *  in `cityRank`'s order, ties in the order given. The caller still filters
 *  (`currentNews`, the search) and caps. */
export function cityLinesToday<T extends NewsForCity>(
  news: readonly T[], now: Date, lines: readonly string[],
): T[] {
  const today = berlinDay(now)
  return news
    .filter(n => !cityWindowEnded(n, now))
    .map((n, i) => ({ n, i, r: cityRank(n, today, lines) }))
    .sort((a, b) => a.r - b.r || a.i - b.i)
    .map(x => x.n)
}

// ---------------------------------------------------------------------------
// Search (N3)
// ---------------------------------------------------------------------------

/**
 * Does this row answer the query? `search.ts`'s `matchesSearch`, under the
 * name this file's callers and tests already use — one haystack for Today and
 * Browse, so the two boxes cannot disagree about what a row says (a1-05:
 * "Bresson" found nothing here while film rows printed "Robert Bresson").
 * Every whitespace-separated word has to land; a blank query matches
 * everything.
 */
export function matchesQuery(e: Searchable, query: string): boolean {
  return matchesSearch(e, query)
}

/**
 * `Fri 2 Oct` — weekday, day, month, the one way the app writes a day.
 *
 * It used to be written three ways: `Fri 2 Oct` (ShowingChips, which stripped
 * the comma), `Fri, 2 Oct` (the card, the sheet's Opening fact, the thread
 * list) and `until Wed, 30 Sept` (the Year's headers). The comma is not
 * ours: `en-GB` puts one there in Chrome and not in Node, so every caller
 * that did not strip it was also saying something different in the browser
 * and in the offline suite. It is stripped here, once.
 *
 * Takes a `YYYY-MM-DD` (read in UTC, so it never shifts a day) or a
 * timestamp, read as the current region's day — a thread's or a message's
 * time. A row's own timestamp is its zone's business (C2.1): that caller
 * hands in `dayIn(at, zoneOfCity(row.city))`, a date.
 */
const shortUtc = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' })
export function fmtDayShort(at: string): string {
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(at)
  const d = D(dateOnly ? at : dayIn(at, hereZone()))
  // A formatter built once, as `zone.ts`'s are (a4-13); 'Invalid Date' is
  // what `toLocaleDateString` answered for a bad input, where `format` throws.
  if (Number.isNaN(d.getTime())) return 'Invalid Date'
  return shortUtc.format(d).replace(',', '')
}
